import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    ColorSwatchRingConstantType,
    ColorSwatchSizeConstantType,
    ColorSwatchDynamicStyleType,
    ColorSwatchMeasures,
    ColorSwatchSize
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getFocusRingStyle
} from "../../helpers/theme/state";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    webStyle
} from "../../utils";

export const COLOR_SWATCH_DISABLED_OPACITY = 0.38;

export const getColorSwatchSize = ({
    controlHeights,
    iconSizes,
    spaces,
    size
}: ColorSwatchSizeConstantType): ColorSwatchMeasures => {
    const sizes: Record<ColorSwatchSize, ColorSwatchMeasures> = {
        small: {
            iconSize: iconSizes.small - spaces.spacingXs,
            size: iconSizes.large
        },
        medium: {
            size: iconSizes.large + spaces.spacingXs,
            iconSize: iconSizes.small
        },
        large: {
            size: controlHeights.small,
            iconSize: iconSizes.large
        }
    };

    return sizes[size];
};

export const getColorSwatchRingColor = ({
    isFocusVisible,
    isSelected,
    isHovered,
    colors
}: ColorSwatchRingConstantType): string | null => {
    if(isFocusVisible) {
        return null;
    }

    if(isSelected) {
        return colors.content.text.high;
    }

    if(isHovered) {
        return colors.content.icon.low;
    }

    return null;
};

export const useStyles = ({
    isFocusVisible,
    shapeConfigs,
    currentSize,
    isDisabled,
    isSelected,
    isHovered,
    radiuses,
    borders,
    surface,
    colors,
    isEdge,
    fill
}: ColorSwatchDynamicStyleType) => {
    const ringColor = getColorSwatchRingColor({
        isFocusVisible,
        isSelected,
        isHovered,
        colors
    });

    const corner = getCornerStyle({
        radius: radiuses.full,
        shapeConfigs
    });

    const container = {
        ...corner,
        ...getFocusRingStyle({
            colorKey: "default",
            isFocusVisible,
            borders,
            colors
        }),
        opacity: isDisabled ? COLOR_SWATCH_DISABLED_OPACITY : 1,
        boxShadow: ringColor ? [
            {
                spreadDistance: borders.focus,
                color: surface,
                offsetX: 0,
                offsetY: 0
            },
            {
                spreadDistance: borders.focus * 2,
                color: ringColor,
                offsetX: 0,
                offsetY: 0
            }
        ] : [],
        height: currentSize.size,
        width: currentSize.size
    } as Mutable<ViewStyle>;

    const clip = {
        ...corner
    } as Mutable<ViewStyle>;

    const color = {
        backgroundColor: fill
    } as Mutable<ViewStyle>;

    const edge = {
        ...corner,
        borderColor: isEdge ? colors.content.border.default : "transparent",
        borderWidth: borders.line
    } as Mutable<ViewStyle>;

    return {
        container,
        color,
        clip,
        edge
    };
};

const stylesheet = StyleSheet.create({
    container: {
        justifyContent: "center",
        alignItems: "center",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    clip: {
        justifyContent: "center",
        position: "absolute",
        alignItems: "center",
        overflow: "hidden",
        bottom: 0,
        right: 0,
        left: 0,
        top: 0
    },
    layer: {
        pointerEvents: "none",
        position: "absolute",
        bottom: 0,
        right: 0,
        left: 0,
        top: 0
    }
});

export default stylesheet;
